import React, { useState, useEffect } from "react";
import apiClient from "../../../../api/apiClient";
import Loading from "../../../../components/Loading/Loading";
import LeadStatusBadge from "./shared/LeadStatusBadge";
import PipelineStageTag from "./shared/PipelineStageTag";
import ActivityTimeline from "./shared/ActivityTimeline";
import LeadFormModal from "./modals/LeadFormModal";
import AddActivityModal from "./modals/AddActivityModal";
import ConvertLeadModal from "./modals/ConvertLeadModal";
import LinkLicenseModal from "./modals/LinkLicenseModal";
import MergeLeadModal from "./modals/MergeLeadModal";
import { leadContactName, leadDisplayName, leadSourceLabel } from "./shared/crmConstants";
import { PRODUCT_TYPES, partnerTypeLabel } from "../../../../constants/supplyChain";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : "—";
const fmtVal  = (v) => v != null ? `$${Number(v).toLocaleString()}` : "—";

const InfoRow = ({ label, children }) => (
    <div className="crm-lead-info-row">
        <span className="crm-lead-info-label">{label}</span>
        <span className="crm-lead-info-value">{children}</span>
    </div>
);

const LeadDetail = ({ leadId, stages, admins, onBack, userProfile, currentUserId, baseEndpoint = "/admin/crm/leads" }) => {
    const [data, setData]               = useState(null);
    const [loading, setLoading]         = useState(true);
    const [fetchError, setFetchError]   = useState(null);
    const [activeSubTab, setActiveSubTab] = useState("activities");
    const [showEdit, setShowEdit]       = useState(false);
    const [showActivity, setShowActivity] = useState(false);
    const [editActivity, setEditActivity] = useState(null);
    const [showConvert, setShowConvert] = useState(false);
    const [showLinkLicense, setShowLinkLicense] = useState(false);
    const [showMerge, setShowMerge] = useState(false);

    const fetchLead = async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const res = await apiClient.get(`${baseEndpoint}/${leadId}`);
            setData(res.data);
        } catch (err) {
            setFetchError(err.response?.data?.error || err.message || "Failed to load lead");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLead(); }, [leadId]);

    const handleDeleteActivity = async (activityId) => {
        if (!window.confirm("Delete this activity?")) return;
        try {
            await apiClient.delete(`/admin/crm/activities/${activityId}`);
            fetchLead();
        } catch (err) {
            alert(err.response?.data?.error || "Failed to delete activity");
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Permanently delete this lead? This cannot be undone.")) return;
        try {
            await apiClient.delete(`${baseEndpoint}/${leadId}`);
            onBack();
        } catch (err) {
            alert(err.response?.data?.error || "Failed to delete lead");
        }
    };

    if (loading) return <div className="admin-loading-wrapper"><Loading /></div>;
    if (fetchError || !data) return (
        <div className="crm-detail-error">
            <button className="crm-back-btn" onClick={onBack}>← Back to Leads</button>
            <div className="crm-email-banner crm-email-banner--error">
                {fetchError || "Lead not found."}
            </div>
        </div>
    );

    const { lead, activities, tags, citations = [], convertedLicense } = data;
    const activeCitations = citations.filter((c) => !c.endedAt);
    const isSuperadmin = userProfile === "superadmin";

    return (
        <div className="crm-detail-wrapper">
            <button className="crm-back-btn" onClick={onBack}>← Back to Leads</button>

            {/* ── Header card ── */}
            <div className="crm-detail-header">
                <div className="crm-detail-header__identity">
                    <h2 className="crm-detail-header__name">{leadDisplayName(lead)}</h2>
                    <div className="crm-detail-header__badges">
                        <LeadStatusBadge status={lead.status} />
                        {lead.stageName && <PipelineStageTag name={lead.stageName} color={lead.stageColor} />}
                        <span className={`crm-priority-badge crm-priority-badge--${lead.priority}`}>{lead.priority}</span>
                    </div>
                    {(lead.businessType || leadContactName(lead) || lead.jobTitle) && (
                        <p className="crm-detail-header__sub">
                            {[partnerTypeLabel(lead.businessType), leadContactName(lead), lead.jobTitle].filter(Boolean).join(" · ")}
                        </p>
                    )}
                </div>
                <div className="crm-detail-header__actions">
                    <button className="admin-btn" onClick={() => setShowEdit(true)}>Edit Lead</button>
                    <button className="admin-btn admin-btn--primary" onClick={() => setShowActivity(true)}>+ Log Activity</button>
                    {!lead.convertedLicenseNo && (
                        <button className="admin-btn" onClick={() => setShowLinkLicense(true)}>Link to DYC license…</button>
                    )}
                    <button className="admin-btn" onClick={() => setShowMerge(true)}>Merge into…</button>
                    {!lead.convertedAt && (
                        <button className="admin-btn" onClick={() => setShowConvert(true)}>Convert to User</button>
                    )}
                    <button className="admin-btn admin-btn--danger" onClick={handleDelete}>Delete</button>
                </div>
            </div>

            {/* ── Body ── */}
            <div className="crm-lead-detail">
                {/* Sidebar */}
                <div className="crm-lead-detail__sidebar">
                    {convertedLicense && (
                        <InfoRow label="DYC license">
                            {convertedLicense.company} ({convertedLicense.licenseNo})
                            {lead.licenseLinkedAt ? ` · linked ${fmtDate(lead.licenseLinkedAt)}` : ""}
                        </InfoRow>
                    )}
                    {lead.company    && <InfoRow label="Company">{lead.company}</InfoRow>}
                    {lead.businessType && <InfoRow label="Business type">{partnerTypeLabel(lead.businessType)}</InfoRow>}
                    {lead.usdaLicenseNumber && <InfoRow label="USDA #">{lead.usdaLicenseNumber}</InfoRow>}
                    {lead.ein        && <InfoRow label="EIN">{lead.ein}</InfoRow>}
                    {lead.email      && <InfoRow label="Email">{lead.email}</InfoRow>}
                    {lead.phone      && <InfoRow label="Phone">{lead.phone}</InfoRow>}
                    {lead.jobTitle   && <InfoRow label="Job Title">{lead.jobTitle}</InfoRow>}
                    {lead.industry   && <InfoRow label="Industry">{lead.industry}</InfoRow>}
                    <InfoRow label="Source">
                        {leadSourceLabel(lead.source) || "—"}
                    </InfoRow>
                    {(lead.activeCitationCount > 0 || lead.unverifiedCitationCount > 0) && (
                        <InfoRow label="Cited by">
                            {lead.activeCitationCount} DYC license{lead.activeCitationCount === 1 ? "" : "s"}
                            {lead.unverifiedCitationCount > 0 ? ` (+${lead.unverifiedCitationCount} unverified)` : ""}
                        </InfoRow>
                    )}
                    {lead.estimatedValue != null && (
                        <InfoRow label="Est. Value">{fmtVal(lead.estimatedValue)}</InfoRow>
                    )}
                    {lead.estimatedCloseDate && (
                        <InfoRow label="Est. Close">{fmtDate(lead.estimatedCloseDate)}</InfoRow>
                    )}
                    <InfoRow label="Assigned To">
                        {lead.assignedFirstName
                            ? `${lead.assignedFirstName} ${lead.assignedLastName}`
                            : <span style={{ color: "var(--color-text-secondary)" }}>Unassigned</span>
                        }
                    </InfoRow>
                    {(lead.city || lead.state) && (
                        <InfoRow label="Location">
                            {[lead.city, lead.state].filter(Boolean).join(", ")}
                        </InfoRow>
                    )}
                    {lead.website && (
                        <div className="crm-lead-info-row">
                            <span className="crm-lead-info-label">Website</span>
                            <a href={lead.website} target="_blank" rel="noopener noreferrer" className="crm-lead-name-link crm-lead-info-value">
                                {lead.website}
                            </a>
                        </div>
                    )}
                    {lead.linkedIn && (
                        <div className="crm-lead-info-row">
                            <span className="crm-lead-info-label">LinkedIn</span>
                            <a href={lead.linkedIn} target="_blank" rel="noopener noreferrer" className="crm-lead-name-link crm-lead-info-value">
                                View Profile
                            </a>
                        </div>
                    )}
                    {lead.convertedAt && (
                        <InfoRow label="Converted">{fmtDate(lead.convertedAt)}</InfoRow>
                    )}
                    <InfoRow label="Created">
                        {fmtDate(lead.createdAt)} by {lead.source === "supply_chain" && lead.createdByFirstName === "DYC"
                            ? "supply-chain reporting"
                            : `${lead.createdByFirstName || ""} ${lead.createdByLastName || ""}`.trim() || "—"}
                    </InfoRow>
                    {tags.length > 0 && (
                        <div className="crm-lead-info-row">
                            <span className="crm-lead-info-label">Tags</span>
                            <div className="crm-tag-list">
                                {tags.map((t) => (
                                    <span key={t.id} className="crm-stage-tag" style={{ backgroundColor: t.color }}>{t.name}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {lead.notes && (
                        <div className="crm-lead-info-row">
                            <span className="crm-lead-info-label">Notes</span>
                            <span className="crm-lead-info-value crm-lead-notes">{lead.notes}</span>
                        </div>
                    )}
                </div>

                {/* Main panel */}
                <div className="crm-lead-detail__main">
                    <div className="crm-sub-tab-bar">
                        <button
                            className={`crm-sub-tab-btn ${activeSubTab === "activities" ? "active" : ""}`}
                            onClick={() => setActiveSubTab("activities")}
                        >
                            Activities ({activities.length})
                        </button>
                        <button
                            className={`crm-sub-tab-btn ${activeSubTab === "citations" ? "active" : ""}`}
                            onClick={() => setActiveSubTab("citations")}
                        >
                            Reported by ({activeCitations.length})
                        </button>
                    </div>

                    {activeSubTab === "citations" && <CitationList citations={citations} />}

                    {activeSubTab === "activities" && (
                        <ActivityTimeline
                            activities={activities}
                            onEdit={(a) => setEditActivity(a)}
                            onDelete={handleDeleteActivity}
                            currentUserId={currentUserId}
                            isSuperadmin={isSuperadmin}
                        />
                    )}
                </div>
            </div>

            {showEdit && (
                <LeadFormModal
                    lead={lead}
                    stages={stages}
                    admins={admins}
                    baseEndpoint={baseEndpoint}
                    onClose={() => setShowEdit(false)}
                    onSaved={() => { setShowEdit(false); fetchLead(); }}
                />
            )}
            {(showActivity || editActivity) && (
                <AddActivityModal
                    leadId={leadId}
                    activity={editActivity}
                    onClose={() => { setShowActivity(false); setEditActivity(null); }}
                    onSaved={(newActivity) => {
                        setShowActivity(false);
                        setEditActivity(null);
                        if (newActivity) {
                            // Prepend directly — no refetch needed
                            setData((prev) => ({
                                ...prev,
                                activities: [newActivity, ...(prev?.activities || [])],
                            }));
                        } else {
                            fetchLead();
                        }
                    }}
                />
            )}
            {showLinkLicense && (
                <LinkLicenseModal
                    lead={lead}
                    onClose={() => setShowLinkLicense(false)}
                    onLinked={() => { setShowLinkLicense(false); fetchLead(); }}
                />
            )}
            {showMerge && (
                <MergeLeadModal
                    lead={lead}
                    onClose={() => setShowMerge(false)}
                    onMerged={() => { setShowMerge(false); onBack(); }}
                />
            )}
            {showConvert && (
                <ConvertLeadModal
                    lead={lead}
                    onClose={() => setShowConvert(false)}
                    onConverted={() => fetchLead()}
                />
            )}
        </div>
    );
};

// Who reported this business (supply-chain citations): relation, product types and the
// contact details each reporter supplied.
const CitationList = ({ citations }) => {
    if (!citations.length) {
        return <p className="crm-lead-info-value" style={{ padding: "var(--space-3)" }}>No DYC license has reported this business.</p>;
    }
    return (
        <table className="admin-table">
            <thead>
                <tr>
                    <th>Reported by</th>
                    <th>As their</th>
                    <th>Products</th>
                    <th>Contact supplied</th>
                    <th>Since</th>
                </tr>
            </thead>
            <tbody>
                {citations.map((c) => (
                    <tr key={c.id} style={c.endedAt ? { opacity: 0.55 } : undefined}>
                        <td>
                            {c.reporterCompany || c.reporterLicenseNo}
                            {c.unverified && <span className="crm-priority-badge crm-priority-badge--warm" style={{ marginLeft: 4 }}>Unverified</span>}
                            {c.endedAt && <span className="crm-priority-badge" style={{ marginLeft: 4 }}>Ended</span>}
                        </td>
                        <td>{c.relation}</td>
                        <td>{(c.productTypes || []).filter((t) => PRODUCT_TYPES.includes(t)).join(", ") || "—"}</td>
                        <td>{[c.contactName, c.contactEmail, c.contactPhone].filter(Boolean).join(" · ") || "—"}</td>
                        <td>{fmtDate(c.createdAt)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default LeadDetail;
